import { FormSectionHandler } from '../entity.handler';
import { SoftLimits } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { NumberField } from '../utils';

interface EditorValues {
    [key: string]: number;
}

interface LimitedCompanyResponse {
    id?: string;
    limits?: SoftLimits;
}

export class SoftLimitSectionHandler<T extends LimitedCompanyResponse> implements FormSectionHandler<T, EditorValues> {

    type = 'form';
    icon = 'arrow-alt-to-top';
    bulk = true;
    hidden = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public entityType: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SHARED.LIMITS.ENTITIES.${key}`, values);
    }

    applyUpdate(entity: T, payload: any): Promise<any> {
        switch (this.entityType) {
            case 'client': return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, payload));
            case 'vendor': return this.admin.applyUpdate(api => api.accounts.updateVendor(entity.id, payload));
            case 'distributor': return this.admin.applyUpdate(api => api.accounts.updateDistributor(entity.id, payload));
            case 'companygroup': return this.admin.applyUpdate(api => api.accounts.updateCompanyGroup(entity.id, payload));
            default:
                throw new Error(`Update for '${this.entityType}' not supported`);
        }
    }

    mapEntityToEditor(entity: LimitedCompanyResponse): Promise<EditorValues> {
        const result = {};
        Object.keys(entity.limits.entities).map(key => {
            result[key] = entity.limits.entities[key].active; // we're only doing the active value for now
        });
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: LimitedCompanyResponse, values: EditorValues): Promise<LimitedCompanyResponse> {
        const result = {};
        Object.keys(values).map(key => {
            result[key] = { active: values[key], total: values[key] * 2 }; // we're only doing the active value for now, assume a total of active * 2 for now
        });
        entity.limits = entity.limits || {} as any;
        entity.limits.entities = result;
        return Promise.resolve(entity);
    }

    async getFields(entity: LimitedCompanyResponse): Promise<FormBuilderField[]> {
        // to support bulk edits we need to hack the limit list since it's passed on the entity, and during bulk editing we do not have the entity
        // this has the side effect that we need to click on a single entity before this will work, but since it's damned near impossible to not 
        // do that in the UI, this should work reliably enough
        const fields = await this.admin.getCached<FormBuilderField[]>(`entity.limits.${this.entityType}`, async () => {
            if (entity) {
                return Object.keys(entity.limits.entities).map(key => {
                    return NumberField(key, this.admin.translate(`ENTITIES.${key.toUpperCase()}`), false, 0, entity.limits.entities[key].max, null);
                });
            } else {
                return null;
            }
        });
        this.hidden = (!fields || fields.length === 0);
        return fields?.sort((a, b) => a.title.localeCompare(b.title)) || [];
    }

}
